import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../components/common/EmptyState';
import { SkeletonTable } from '../../components/common/SkeletonTable';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';

interface AutoCloseStatusDto {
  lastRunAt?: string;
  nextRunAt?: string;
  isEnabled: boolean;
  closedIncidentsLastRun: number;
  averageProcessingTime: number;
}

interface AutoCloseSettingsShape {
  general?: {
    autoCloseDays?: number;
    autoCloseEnabled?: boolean;
    autoCloseFrequencyMinutes?: number;
  };
}

interface IncidentRow {
  id: string;
  number?: string;
  title?: string;
  createdAt: string;
  resolvedAt?: string;
  closedAt?: string;
}

function toIso(date: Date): string {
  return date.toISOString();
}

function getClosedRange(hours: number): { closedFrom: string; closedTo: string } {
  const now = new Date();
  return {
    closedFrom: toIso(new Date(now.getTime() - hours * 60 * 60 * 1000)),
    closedTo: toIso(now),
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return 'An unexpected error occurred while loading data.';
}

function asIncidentRows(
  value: unknown,
): IncidentRow[] {
  if (!value || typeof value !== 'object') return [];
  if ('incidents' in value && Array.isArray((value as { incidents?: unknown }).incidents)) {
    return (value as { incidents: IncidentRow[] }).incidents;
  }
  if (Array.isArray(value)) return value as IncidentRow[];
  return [];
}

function getTotalCount(
  value: unknown,
): number {
  if (!value || typeof value !== 'object') return 0;
  const totalCount = (value as { totalCount?: unknown }).totalCount;
  return typeof totalCount === 'number' && Number.isFinite(totalCount) ? totalCount : 0;
}

function asScheduledJobs(value: unknown): Array<{ name?: string; frequency?: string }> {
  if (Array.isArray(value)) return value as Array<{ name?: string; frequency?: string }>;
  if (value && typeof value === 'object' && 'items' in value && Array.isArray((value as { items?: unknown }).items)) {
    return (value as { items: Array<{ name?: string; frequency?: string }> }).items;
  }
  return [];
}

function formatDuration(valueMs: number): string {
  if (!Number.isFinite(valueMs) || valueMs <= 0) return '—';

  const totalMinutes = Math.round(valueMs / 60_000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatFrequency(minutes?: number): string {
  if (!minutes || minutes <= 0) return 'Unknown';
  if (minutes % 60 === 0) return `Every ${minutes / 60} hour${minutes / 60 === 1 ? '' : 's'}`;
  return `Every ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

function getTimeUntilNext(nextRunAt?: string): string {
  if (!nextRunAt) return 'Unknown';
  const next = new Date(nextRunAt).getTime();
  if (Number.isNaN(next)) return 'Unknown';

  const diff = next - Date.now();
  if (diff <= 0) return 'Overdue';

  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'}`;
  }

  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

export function AutoClosePage() {
  const { user } = useAuth();
  const tenantId = user?.tenantId ?? '';
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  const statusQuery = useQuery({
    queryKey: ['admin-auto-close-status'],
    queryFn: async () => apiClient.getAutoCloseStatus() as Promise<AutoCloseStatusDto>,
    refetchInterval: autoRefreshEnabled ? 60_000 : false,
  });

  const settingsQuery = useQuery({
    queryKey: ['admin-auto-close-settings'],
    queryFn: async () => apiClient.getSystemSettings() as Promise<AutoCloseSettingsShape>,
    refetchInterval: autoRefreshEnabled ? 5 * 60_000 : false,
  });

  const jobsQuery = useQuery({
    queryKey: ['admin-auto-close-jobs'],
    queryFn: () => apiClient.getScheduledJobs(),
    refetchInterval: autoRefreshEnabled ? 5 * 60_000 : false,
  });

  const [last24hQuery, last30dQuery] = useQueries({
    queries: [
      {
        queryKey: ['admin-auto-close-stats', tenantId, '24h'],
        enabled: Boolean(tenantId),
        queryFn: async () => {
          const { closedFrom, closedTo } = getClosedRange(24);
          const result = await apiClient.getTenantIncidents(tenantId, {
            status: 'Closed',
            closedFrom,
            closedTo,
            page: 1,
            pageSize: 1,
          });
          return getTotalCount(result);
        },
      },
      {
        queryKey: ['admin-auto-close-stats', tenantId, '30d'],
        enabled: Boolean(tenantId),
        queryFn: async () => {
          const { closedFrom, closedTo } = getClosedRange(30 * 24);
          const result = await apiClient.getTenantIncidents(tenantId, {
            status: 'Closed',
            closedFrom,
            closedTo,
            page: 1,
            pageSize: 1,
          });
          return getTotalCount(result);
        },
      },
    ],
  });

  const recentIncidentsQuery = useQuery({
    queryKey: ['admin-auto-close-recent-incidents', tenantId, '30d'],
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const { closedFrom, closedTo } = getClosedRange(30 * 24);
      return apiClient.getTenantIncidents(tenantId, {
        status: 'Closed',
        closedFrom,
        closedTo,
        sortBy: 'closedAt_desc',
        page: 1,
        pageSize: 20,
      });
    },
    refetchInterval: autoRefreshEnabled ? 60_000 : false,
  });

  const status = statusQuery.data;
  const settings = settingsQuery.data?.general;
  const recentIncidents = useMemo(
    () => asIncidentRows(recentIncidentsQuery.data),
    [recentIncidentsQuery.data],
  );

  const autoCloseJob = useMemo(() => {
    const rows = asScheduledJobs(jobsQuery.data);
    return rows.find((item) =>
      String(item.name ?? '').toLowerCase().includes('autoclose'),
    );
  }, [jobsQuery.data]);

  const frequencyText = useMemo(() => {
    if (typeof settings?.autoCloseFrequencyMinutes === 'number') {
      return formatFrequency(settings.autoCloseFrequencyMinutes);
    }

    const fromJob = autoCloseJob?.frequency;
    if (typeof fromJob === 'string' && fromJob.trim().length > 0) return fromJob;

    if (status?.lastRunAt && status?.nextRunAt) {
      const lastRun = new Date(status.lastRunAt).getTime();
      const nextRun = new Date(status.nextRunAt).getTime();
      if (!Number.isNaN(lastRun) && !Number.isNaN(nextRun) && nextRun > lastRun) {
        return formatFrequency(Math.round((nextRun - lastRun) / 60_000));
      }
    }

    return 'Unknown';
  }, [autoCloseJob, settings, status]);

  const averageAutoCloseTime = useMemo(() => {
    const values = recentIncidents
      .map((incident) => {
        if (!incident.closedAt || !incident.resolvedAt) return null;
        const closedAt = new Date(incident.closedAt).getTime();
        const resolvedAt = new Date(incident.resolvedAt).getTime();
        if (Number.isNaN(closedAt) || Number.isNaN(resolvedAt) || closedAt <= resolvedAt) return null;
        return closedAt - resolvedAt;
      })
      .filter((value): value is number => value !== null);

    if (values.length === 0) return null;
    return values.reduce((total, value) => total + value, 0) / values.length;
  }, [recentIncidents]);

  const isLoading =
    statusQuery.isLoading ||
    (Boolean(tenantId) && (last24hQuery.isLoading || last30dQuery.isLoading || recentIncidentsQuery.isLoading));

  const blockingError = statusQuery.error;
  const partialErrors = [
    settingsQuery.error,
    jobsQuery.error,
    last24hQuery.error,
    last30dQuery.error,
    recentIncidentsQuery.error,
  ].filter(Boolean);

  const closedLast24hCount = tenantId ? (last24hQuery.data ?? 0) : null;
  const closedLast30dCount = tenantId ? (last30dQuery.data ?? 0) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Auto-Close Monitoring</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Configuration, execution status, and recently closed incidents.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
          <input
            type="checkbox"
            checked={autoRefreshEnabled}
            onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
          />
          Auto-refresh
        </label>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="card animate-pulse">
                <div className="h-3 w-28 rounded bg-gray-300 dark:bg-gray-700" />
                <div className="mt-4 h-7 w-36 rounded bg-gray-300 dark:bg-gray-700" />
              </div>
            ))}
          </div>
          <div className="card">
            <SkeletonTable rows={6} columns={4} />
          </div>
        </div>
      ) : null}

      {!isLoading && blockingError ? (
        <div className="card">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-300">Failed to load auto-close monitoring data</h2>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{getErrorMessage(blockingError)}</p>
          <button
            type="button"
            onClick={() => {
              void statusQuery.refetch();
            }}
            className="mt-4 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            Try again
          </button>
        </div>
      ) : null}

      {!isLoading && !blockingError ? (
        <>
          {partialErrors.length > 0 ? (
            <div className="card border border-yellow-300 dark:border-yellow-700">
              <h2 className="text-base font-semibold text-yellow-800 dark:text-yellow-300">Some metrics could not be loaded</h2>
              <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                {getErrorMessage(partialErrors[0])}
              </p>
            </div>
          ) : null}

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Configuration status</h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">Status</dt>
                <dd className={`mt-1 text-xl font-semibold ${status?.isEnabled ?? settings?.autoCloseEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-300'}`}>
                  {status?.isEnabled ?? settings?.autoCloseEnabled ? 'Enabled' : 'Disabled'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">Days threshold</dt>
                <dd className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {typeof settings?.autoCloseDays === 'number' ? `${settings.autoCloseDays} day${settings.autoCloseDays === 1 ? '' : 's'}` : 'Unknown'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">Frequency</dt>
                <dd className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{frequencyText}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">Last run</dt>
                <dd className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{formatDateTime(status?.lastRunAt)}</dd>
              </div>
            </dl>
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Auto-closed last run</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{(status?.closedIncidentsLastRun ?? 0).toLocaleString()}</p>
            </article>
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Closed last 24h</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {closedLast24hCount === null ? '—' : closedLast24hCount.toLocaleString()}
              </p>
            </article>
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Closed last 30d</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {closedLast30dCount === null ? '—' : closedLast30dCount.toLocaleString()}
              </p>
            </article>
            <article className="card">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg processing time</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {typeof status?.averageProcessingTime === 'number' && status.averageProcessingTime > 0
                  ? `${status.averageProcessingTime.toFixed(1)}s`
                  : averageAutoCloseTime
                    ? formatDuration(averageAutoCloseTime)
                    : '—'}
              </p>
            </article>
          </section>

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Next scheduled run</h2>
            <p className="mt-2 text-base text-gray-900 dark:text-gray-100">{formatDateTime(status?.nextRunAt)}</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Time remaining: {getTimeUntilNext(status?.nextRunAt)}</p>
          </section>

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent auto-closed incidents</h2>
            {!tenantId ? (
              <EmptyState
                title="Tenant context unavailable"
                message="Recent incident details require a tenant in the current session."
              />
            ) : null}
            {tenantId && recentIncidents.length === 0 ? (
              <EmptyState
                title="No recent incidents found"
                message="No closed incidents were returned for the current period."
              />
            ) : null}
            {tenantId && recentIncidents.length > 0 ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-3 font-semibold">Incident ID</th>
                      <th className="px-3 py-3 font-semibold">Title</th>
                      <th className="px-3 py-3 font-semibold">Closed at</th>
                      <th className="px-3 py-3 font-semibold">Age when closed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {recentIncidents.map((incident) => {
                      const closedAtMs = incident.closedAt ? new Date(incident.closedAt).getTime() : Number.NaN;
                      const startAgeAtMs = incident.resolvedAt
                        ? new Date(incident.resolvedAt).getTime()
                        : new Date(incident.createdAt).getTime();
                      const ageMs = Number.isNaN(closedAtMs) || Number.isNaN(startAgeAtMs) ? NaN : closedAtMs - startAgeAtMs;
                      return (
                        <tr key={incident.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                          <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100">{incident.number ?? incident.id}</td>
                          <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{incident.title ?? '—'}</td>
                          <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{formatDateTime(incident.closedAt)}</td>
                          <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{formatDuration(ageMs)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
